
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from '@tanstack/react-query';

const PaymentConfirmation = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'processing' | 'success' | 'failed'>('processing');
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyPaymentAndSubscription = async () => {
      try {
        const paymentIntent = searchParams.get('payment_intent');
        const redirectStatus = searchParams.get('redirect_status');
        const creatorId = searchParams.get('creator_id');
        const creatorName = searchParams.get('creator_name');
        const type = searchParams.get('type') as 'monthly' | 'lifetime';
        const amount = searchParams.get('amount');
        
        console.log('Starting payment verification with parameters:', {
          paymentIntent,
          redirectStatus,
          creatorId,
          type,
          amount,
          timestamp: new Date().toISOString()
        });

        if (!paymentIntent || !creatorId || redirectStatus !== 'succeeded') {
          console.error('Missing required parameters:', { paymentIntent, redirectStatus, creatorId });
          setStatus('failed');
          toast.error('Payment verification failed - missing parameters');
          return;
        }

        // Get current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error('User auth error:', userError);
          throw userError;
        }
        if (!user) {
          console.error('No user found');
          throw new Error('No user found');
        }

        console.log('Current user:', user.id);

        // Double-check if subscription already exists to avoid duplicates
        const { data: existingSubscription, error: existingSubError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('subscriber_id', user.id)
          .eq('subscribed_to_id', creatorId)
          .maybeSingle();

        if (existingSubError) {
          console.error('Error checking existing subscription:', existingSubError);
          throw existingSubError;
        }

        if (existingSubscription) {
          console.log('Subscription already exists:', existingSubscription);
          setStatus('success');
          return;
        }

        // Create subscription record
        console.log('Creating new subscription record...', {
          subscriber_id: user.id,
          subscribed_to_id: creatorId,
          type
        });

        const { data: subscription, error: subscriptionError } = await supabase
          .from('subscriptions')
          .insert({
            subscriber_id: user.id,
            subscribed_to_id: creatorId,
            type: type || 'monthly'
          })
          .select()
          .single();

        if (subscriptionError) {
          console.error('Failed to create subscription:', subscriptionError);
          throw subscriptionError;
        }

        if (!subscription) {
          console.error('No subscription record created');
          throw new Error('Failed to create subscription record');
        }

        console.log('Subscription created successfully:', subscription);

        // Create transaction record
        console.log('Creating transaction record...');
        const { error: transactionError } = await supabase
          .from('subscription_transactions')
          .insert({
            subscriber_id: user.id,
            creator_id: creatorId,
            subscription_type: type || 'monthly',
            status: 'completed',
            amount: amount ? parseInt(amount) / 100 : 0
          });

        if (transactionError) {
          console.error('Transaction error:', transactionError);
          // If transaction fails, rollback subscription
          console.log('Rolling back subscription due to transaction error...');
          await supabase
            .from('subscriptions')
            .delete()
            .match({ id: subscription.id });
          throw transactionError;
        }

        console.log('All records created successfully, invalidating queries...');

        // Force invalidate all relevant queries
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['subscription-status']
          }),
          queryClient.invalidateQueries({
            queryKey: ['subscription-transactions']
          })
        ]);

        setStatus('success');
        toast.success('Subscription successful!');
        
        // Navigate back to the profile page after a short delay
        setTimeout(() => {
          window.location.href = `/${creatorName}`;
        }, 3000);

      } catch (error) {
        console.error('Payment confirmation error:', error);
        if (verificationAttempts < 3) {
          console.log('Retrying subscription creation... Attempt:', verificationAttempts + 1);
          setVerificationAttempts(prev => prev + 1);
          setTimeout(verifyPaymentAndSubscription, 2000);
          return;
        }
        setStatus('failed');
        toast.error('Error updating subscription status');
      }
    };

    verifyPaymentAndSubscription();
  }, [searchParams, queryClient, verificationAttempts]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center">
        <div className="text-center p-8">
          {status === 'processing' && (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
              <p className="text-xl font-medium">
                {verificationAttempts > 0 
                  ? `Verifying subscription... Attempt ${verificationAttempts + 1}/3`
                  : 'Processing your subscription...'
                }
              </p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="flex flex-col items-center gap-4">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
              <p className="text-xl font-medium">Subscription successful!</p>
              <p className="text-gray-500">Redirecting you back...</p>
            </div>
          )}
          
          {status === 'failed' && (
            <div className="flex flex-col items-center gap-4">
              <XCircle className="h-12 w-12 text-red-500" />
              <p className="text-xl font-medium">Subscription failed</p>
              <p className="text-gray-500">Please try again or contact support</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentConfirmation;
